import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Eye, EyeOff, ChevronDown, ChevronUp, Brain, MessageCircle } from 'lucide-react';
import { B2_ACTIVITIES } from '../data/jeuxReflexionB2Data';

function ActivityCard({ activity }) {
  const [answer, setAnswer] = useState('');
  const [showModele, setShowModele] = useState(false);

  return (
    <div className="bg-white border border-[#E8E4DC] rounded-xl overflow-hidden" data-testid={`activity-${activity.id}`}>
      <div className="px-5 py-4 border-b border-[#F3F4F6]">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-full bg-[#1A2F4A] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
            {activity.num}
          </div>
          <h3 className="text-sm font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>
            {activity.title}
          </h3>
        </div>
      </div>

      <div className="px-5 py-4 space-y-3">
        <p className="text-sm text-[#6B4E37] italic">{activity.prompt}</p>

        <textarea
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          placeholder="Écris ta réponse ici..."
          className="w-full border border-[#E8E4DC] rounded-lg px-3 py-2.5 text-sm text-[#1A2F4A] placeholder-[#ccc] focus:outline-none focus:border-[#D4AF37] transition-colors resize-none"
          rows={3}
          data-testid={`input-${activity.id}`}
        />

        <button
          onClick={() => setShowModele(!showModele)}
          className="flex items-center gap-1.5 text-xs font-bold text-[#D4AF37] hover:text-[#b8962e] transition-colors"
          data-testid={`toggle-${activity.id}`}>
          {showModele ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          {showModele ? 'Cacher la réponse' : 'Voir une réponse modèle'}
        </button>

        {showModele && (
          <div className="bg-[#FAF7F0] border border-[#E8E4DC] rounded-lg p-4 space-y-2" data-testid={`modele-${activity.id}`}>
            <p className="text-sm text-[#1A2F4A] italic leading-relaxed">"{activity.modele}"</p>
            <div className="pt-2 border-t border-[#E8E4DC]">
              <p className="text-[10px] text-[#999] uppercase tracking-wider font-bold mb-1">Structures utilisées</p>
              <div className="flex flex-wrap gap-1.5">
                {activity.structures.map((s, i) => (
                  <span key={i} className="text-[10px] bg-[#1A2F4A]/5 text-[#1A2F4A] rounded-full px-2 py-0.5 font-medium">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function JeuxReflexionB2() {
  const [activeBlock, setActiveBlock] = useState('block1');
  const [expandedPart, setExpandedPart] = useState(0);

  const block = B2_ACTIVITIES[activeBlock];

  return (
    <div className="bg-[#FAF7F0] min-h-screen">
      {/* Hero */}
      <section className="pt-16 pb-8">
        <div className="max-w-[600px] mx-auto px-8 text-center">
          <Link to="/speaking-lab" className="inline-flex items-center gap-1 text-xs text-[#999] hover:text-[#D4AF37] transition-colors mb-4" data-testid="back-to-lab">
            <ArrowRight className="w-3 h-3 rotate-180" /> Speaking Lab
          </Link>
          <h1
            className="text-2xl sm:text-3xl font-bold text-[#1A2F4A] mb-2"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="page-title">
            Jeux de réflexion (B2)
          </h1>
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="h-px w-8 bg-[#D4AF37]/50" />
            <Brain className="w-4 h-4 text-[#D4AF37]/60" />
            <div className="h-px w-8 bg-[#D4AF37]/50" />
          </div>
          <p className="text-xs text-[#6B4E37] italic">
            Argumentation, nuance et communication avancée — Préparation DELF B2
          </p>
        </div>
      </section>

      {/* Block Tabs */}
      <section className="pb-4">
        <div className="max-w-[600px] mx-auto px-8 flex gap-2 justify-center" data-testid="block-tabs">
          <button
            onClick={() => { setActiveBlock('block1'); setExpandedPart(0); }}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all ${
              activeBlock === 'block1'
                ? 'bg-[#1A2F4A] text-white'
                : 'bg-white border border-[#E8E4DC] text-[#1A2F4A] hover:border-[#D4AF37]'
            }`}
            data-testid="block-btn-1">
            <Brain className="w-3 h-3" /> Analyse & Réflexion
          </button>
          <button
            onClick={() => { setActiveBlock('block2'); setExpandedPart(0); }}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all ${
              activeBlock === 'block2'
                ? 'bg-[#1A2F4A] text-white'
                : 'bg-white border border-[#E8E4DC] text-[#1A2F4A] hover:border-[#D4AF37]'
            }`}
            data-testid="block-btn-2">
            <MessageCircle className="w-3 h-3" /> Réactions & Communication
          </button>
        </div>
      </section>

      {/* Block Info */}
      <section className="pb-4">
        <div className="max-w-[600px] mx-auto px-8 text-center">
          <p className="text-xs text-[#999]">{block.subtitle}</p>
        </div>
      </section>

      {/* Parts */}
      <section className="pb-20">
        <div className="max-w-[600px] mx-auto px-8 space-y-4">
          {block.parts.map((part, pIdx) => (
            <div key={pIdx}>
              {/* Part header (collapsible) */}
              <button
                onClick={() => setExpandedPart(expandedPart === pIdx ? -1 : pIdx)}
                className="w-full flex items-center justify-between bg-white border border-[#E8E4DC] rounded-xl px-5 py-3 hover:border-[#D4AF37] transition-colors"
                data-testid={`part-toggle-${pIdx}`}>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] flex items-center justify-center text-[10px] font-bold">
                    {pIdx + 1}
                  </div>
                  <span className="text-sm font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {part.title}
                  </span>
                  <span className="text-[10px] text-[#999]">{part.activities.length} activités</span>
                </div>
                {expandedPart === pIdx ? <ChevronUp className="w-4 h-4 text-[#D4AF37]" /> : <ChevronDown className="w-4 h-4 text-[#999]" />}
              </button>

              {/* Activities */}
              {expandedPart === pIdx && (
                <div className="mt-3 space-y-3" data-testid={`part-content-${pIdx}`}>
                  {part.activities.map(activity => (
                    <ActivityCard key={activity.id} activity={activity} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
